import { useCallback, useEffect, useRef, useState } from 'react';
import { cx } from '../internal/tone';

/** Subconjunto da Web Speech API que usamos. Não há tipos padrão para isto. */
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: unknown) => void) | null;
  onerror: ((event: unknown) => void) | null;
  onend: (() => void) | null;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** Retira o texto do evento sem depender dos tipos do navegador. */
function extractTranscript(event: unknown): string {
  const e = event as {
    results?: ArrayLike<ArrayLike<{ transcript?: string }>>;
    resultIndex?: number;
  };
  const results = e.results;
  if (!results || results.length === 0) return '';
  const index = typeof e.resultIndex === 'number' ? e.resultIndex : results.length - 1;
  const result = results[index] ?? results[results.length - 1];
  if (!result || result.length === 0) return '';
  return (result[0]?.transcript ?? '').trim();
}

const ERROR_MESSAGES: Record<string, string> = {
  'not-allowed': 'O microfone está bloqueado. Autorize o microfone nas configurações do celular.',
  'service-not-allowed': 'O microfone está bloqueado. Autorize o microfone nas configurações.',
  'no-speech': 'Não ouvi nada. Toque outra vez e fale logo a seguir.',
  network: 'Sem ligação para reconhecer a voz. Escreva à mão, por agora.',
  aborted: 'A gravação foi interrompida. Toque outra vez para tentar de novo.',
};

export interface VoiceButtonProps {
  onResult: (text: string) => void;
  onError?: (e: Error) => void;
  /** Força o estado visual de escuta (útil quando quem manda é a tela). */
  listening?: boolean;
  /** Rótulo acessível do botão em repouso. */
  label?: string;
  className?: string;
}

/**
 * Botão de ditar.
 *
 * Se o navegador não tiver Web Speech API, NÃO desenha nada. Um botão que não
 * faz nada quando se toca é pior do que botão nenhum — ela ia pensar que a
 * aplicativo está avariada.
 *
 * A escuta tem de ser inconfundível: o botão fica vermelho, o anel pulsa e
 * aparece "A ouvir…" por escrito.
 */
export function VoiceButton({
  onResult,
  onError,
  listening,
  label = 'Ditar por voz',
  className,
}: VoiceButtonProps) {
  const [supported] = useState(() => getRecognitionCtor() !== null);
  const [internalListening, setInternalListening] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);

  const isListening = listening ?? internalListening;

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
    setInternalListening(false);
  }, []);

  // Um reconhecimento a correr depois do componente sair fica com o microfone.
  useEffect(() => {
    return () => {
      recognitionRef.current?.abort();
      recognitionRef.current = null;
    };
  }, []);

  const start = useCallback(() => {
    const Ctor = getRecognitionCtor();
    if (!Ctor) return;

    setMessage(null);
    const recognition = new Ctor();
    recognitionRef.current = recognition;
    recognition.lang = 'pt-BR';
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      const text = extractTranscript(event);
      if (text) onResult(text);
    };
    recognition.onerror = (event) => {
      const code = (event as { error?: string }).error ?? 'desconhecido';
      const friendly = ERROR_MESSAGES[code] ?? 'Não consegui ouvir. Tente outra vez.';
      setMessage(friendly);
      setInternalListening(false);
      onError?.(new Error(friendly));
    };
    recognition.onend = () => {
      setInternalListening(false);
    };

    try {
      recognition.start();
      setInternalListening(true);
    } catch (err) {
      const friendly = 'Não consegui ligar o microfone. Tente outra vez.';
      setMessage(friendly);
      setInternalListening(false);
      onError?.(err instanceof Error ? err : new Error(friendly));
    }
  }, [onResult, onError]);

  // Degradação limpa: sem API, não existe botão nenhum.
  if (!supported) return null;

  return (
    <div className={cx('flos-voice-wrap', className)}>
      <button
        type="button"
        className={cx('flos-voice', isListening && 'flos-voice--listening')}
        aria-pressed={isListening}
        aria-label={isListening ? 'A ouvir. Toque para parar.' : label}
        onClick={() => (isListening ? stop() : start())}
      >
        <span className="flos-icon" aria-hidden="true">
          {isListening ? '⏹️' : '🎤'}
        </span>
      </button>
      <span className="flos-voice__status" aria-live="polite">
        {isListening ? 'A ouvir…' : ''}
      </span>
      {message ? (
        <span className="flos-field__error" role="alert">
          <span className="flos-icon" aria-hidden="true">
            ⛔
          </span>
          <span>{message}</span>
        </span>
      ) : null}
    </div>
  );
}
